import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { tagsApi } from '@/api/tagsApi'
import type { Tag } from '@/api/tagsApi'
import { TagForm, type TagFormData } from '@/components/tags/TagForm'
import { TagCard } from '@/components/tags/TagCard'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'
import { formatDateDMY } from '@/utils/date'
import {
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Collapse,
  TablePagination,
  useMediaQuery,
  Breadcrumbs,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import '@/styles/dashboard.css'

export function TagsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  /**
   * Слова тега — это список слов с готовым фильтром, а не отдельная страница:
   * фильтрация по тегам на «Словах» уже есть, и вторая витрина тех же данных
   * разошлась бы с ней по возможностям.
   */
  const openTagWords = useCallback(
    (tagId: number) => {
      navigate(`${ROUTES.WORDS}?tags=${tagId}`)
    },
    [navigate],
  )
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  useAuthStore((s) => s.user)

  const [tags, setTags] = useState<Tag[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    per_page: 20,
  })
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null)
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null)
  const [actionsTag, setActionsTag] = useState<Tag | null>(null)
  const [formLoading, setFormLoading] = useState(false)
  const [systemOnly, setSystemOnly] = useState(false)

  /**
   * Счётчик для кнопки «Фильтры»: в свёрнутой панели живёт единственный
   * переключатель «системные». Поиск не считаем — его поле стоит в тулбаре
   * и видно всегда.
   */
  const appliedFiltersCount = useMemo(() => (systemOnly ? 1 : 0), [systemOnly])

  const fetchTags = useCallback(
    (overrides?: { page?: number; per_page?: number }) => {
      setIsLoading(true)
      setError(null)
      const page = overrides?.page ?? pagination.currentPage
      const per_page = overrides?.per_page ?? pagination.per_page
      tagsApi
        .getList({
          search: appliedSearch || undefined,
          page,
          per_page,
          system: systemOnly || undefined,
        })
        .then((res) => {
          setTags(res.data)
          setPagination({
            currentPage: res.current_page,
            lastPage: res.last_page,
            total: res.total,
            per_page: res.per_page ?? pagination.per_page,
          })
        })
        .catch((err: Error & { response?: { data?: { message?: string } } }) => {
          setError(err.response?.data?.message || err.message || t('common.error'))
        })
        .finally(() => setIsLoading(false))
    },
    [appliedSearch, pagination.currentPage, pagination.per_page, systemOnly, t]
  )

  useEffect(() => {
    const timer = window.setTimeout(() => setAppliedSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    fetchTags({ page: 1 })
  }, [fetchTags])

  const handlePageChange = (_: unknown, newPage: number) => {
    fetchTags({ page: newPage + 1 })
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const perPage = parseInt(e.target.value, 10)
    setPagination((p) => ({ ...p, per_page: perPage }))
    fetchTags({ page: 1, per_page: perPage })
  }

  const handleCreateTag = async (data: TagFormData) => {
    setFormLoading(true)
    setError(null)
    try {
      await tagsApi.create({ name: data.name, color: data.color })
      setIsCreateModalOpen(false)
      fetchTags()
    } catch (err) {
      const e = err as Error & { response?: { status?: number; data?: { message?: string } } }
      if (e.response?.status === 403) {
        setError(e.response?.data?.message || t('tags.subscriptionLimit'))
      } else {
        setError(e.response?.data?.message || e.message || t('common.error'))
      }
      throw e
    } finally {
      setFormLoading(false)
    }
  }

  const handleEditTag = async (data: TagFormData) => {
    if (!selectedTag) return
    setFormLoading(true)
    setError(null)
    try {
      await tagsApi.update(selectedTag.id, { name: data.name, color: data.color })
      setIsEditModalOpen(false)
      setSelectedTag(null)
      fetchTags()
    } catch (err) {
      const e = err as Error & { response?: { status?: number; data?: { message?: string } } }
      setError(e.response?.data?.message || e.message || t('common.error'))
      throw e
    } finally {
      setFormLoading(false)
    }
  }

  const handleDeleteTag = async (tag: Tag) => {
    if (!window.confirm(t('tags.deleteConfirm'))) return
    try {
      await tagsApi.delete(tag.id)
      setActionsTag(null)
      setActionsAnchorEl(null)
      fetchTags()
    } catch (err) {
      const e = err as Error & { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message || e.message || t('common.error'))
    }
  }

  const openActionsMenu = (event: React.MouseEvent<HTMLElement>, tag: Tag) => {
    event.stopPropagation()
    setActionsAnchorEl(event.currentTarget)
    setActionsTag(tag)
  }

  const closeActionsMenu = () => {
    setActionsAnchorEl(null)
    setActionsTag(null)
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Typography
            component="a"
            href={ROUTES.DASHBOARD}
            onClick={(e) => {
              e.preventDefault()
              window.location.href = ROUTES.DASHBOARD
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Typography>
          <Typography color="text.primary">{t('tags.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('tags.title')}
          </Typography>
          <PageHelpPopover pageKey="tags" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('tags.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 120 : 200, flex: isMobile ? 1 : undefined }}
          />
          <FiltersButton
            open={filtersOpen}
            count={appliedFiltersCount}
            onToggle={() => setFiltersOpen((o) => !o)}
            label={t('tags.filtersButton')}
          />
          <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateModalOpen(true)}
              sx={{ height: 40 }}
            >
              {t('tags.create')}
            </Button>
          </Box>
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              size="small"
              variant={systemOnly ? 'contained' : 'outlined'}
              onClick={() => setSystemOnly((v) => !v)}
              sx={{ height: 40 }}
            >
              {t('tags.systemTag')}
            </Button>
          </Box>
        </Collapse>

        {isLoading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Paper key={i}>
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="90%" />
                    <Skeleton variant="text" width="40%" />
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('tags.name')}</TableCell>
                    <TableCell>{t('tags.color')}</TableCell>
                    <TableCell>{t('tags.wordsCount')}</TableCell>
                    <TableCell>{t('tags.createdAt')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell><Skeleton variant="text" width={40} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="right"><Skeleton variant="circular" width={32} height={32} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : tags.length === 0 ? (
          <Paper>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {t('tags.empty')}
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setIsCreateModalOpen(true)}
                sx={{ height: 40 }}
              >
                {t('tags.createFirst')}
              </Button>
            </Box>
          </Paper>
        ) : (
          <>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {tags.map((tag) => (
                  <TagCard
                    key={tag.id}
                    tag={tag}
                    onEdit={(t) => {
                      setSelectedTag(t)
                      setIsEditModalOpen(true)
                    }}
                    onDelete={tag.owner_id == null ? undefined : handleDeleteTag}
                    onOpen={(item) => openTagWords(item.id)}
                  />
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('tags.name')}</TableCell>
                      <TableCell>{t('tags.color')}</TableCell>
                      <TableCell>{t('tags.wordsCount')}</TableCell>
                      <TableCell>{t('tags.createdAt')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tags.map((tag) => (
                      <TableRow
                        key={tag.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openTagWords(tag.id)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: 1,
                                backgroundColor: tag.color ?? '#6c757d',
                                border: '1px solid',
                                borderColor: 'divider',
                              }}
                            />
                            <Typography variant="body2" fontWeight={600}>
                              {tag.name}
                            </Typography>
                            {tag.owner_id == null && (
                              <Typography variant="caption" color="text.secondary">
                                ({t('tags.systemTag')})
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              backgroundColor: tag.color ?? '#6c757d',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                            title={tag.color ?? '#6c757d'}
                          />
                        </TableCell>
                        <TableCell>{tag.words_count ?? 0}</TableCell>
                        <TableCell>{formatDateDMY(tag.created_at)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              // Иначе клик по «⋮» сначала открыл бы меню, а
                              // потом уехал на список слов.
                              e.stopPropagation()
                              openActionsMenu(e, tag)
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                mt: 2,
                justifyContent: 'flex-end',
                minWidth: 0,
              }}
            >
              <TablePagination
                component="div"
                count={pagination.total}
                page={Math.max(0, pagination.currentPage - 1)}
                onPageChange={handlePageChange}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage={isMobile ? '' : t('tags.rows_per_page')}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} ${t('tags.of')} ${count !== -1 ? count : `${t('tags.more_than')} ${to}`}`
                }
              />
            </Box>
          </>
        )}

        <Menu
          anchorEl={actionsAnchorEl}
          open={Boolean(actionsAnchorEl)}
          onClose={closeActionsMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              if (actionsTag) {
                setSelectedTag(actionsTag)
                setIsEditModalOpen(true)
                closeActionsMenu()
              }
            }}
          >
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.edit')}
          </MenuItem>
          {actionsTag && actionsTag.owner_id !== null && (
            <MenuItem
              onClick={() => {
                if (actionsTag) {
                  handleDeleteTag(actionsTag)
                }
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
              {t('common.delete')}
            </MenuItem>
          )}
        </Menu>

        <Dialog
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('tags.create')}</DialogTitle>
          <DialogContent>
            <TagForm
              onSubmit={handleCreateTag}
              onCancel={() => setIsCreateModalOpen(false)}
              isLoading={formLoading}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setSelectedTag(null)
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('common.edit')}</DialogTitle>
          <DialogContent>
            {selectedTag && (
              <TagForm
                initialValues={selectedTag}
                onSubmit={handleEditTag}
                onCancel={() => {
                  setIsEditModalOpen(false)
                  setSelectedTag(null)
                }}
                isLoading={formLoading}
              />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
